import { useEffect, useMemo, useState } from 'react';
import { getClientLabel } from '../../utils/formHelpers';
import { formatFileSize } from '../../utils/formatters';

function CreateInvoiceModal({
  open,
  onClose,
  onSubmit,
  createStep,
  onNextStep,
  onPrevStep,
  createForm,
  onFormChange,
  onWhatsappBlur,
  contactOptions,
  metodoPagoOptions,
  estadoOptions,
  formaPagoOptions,
  clientSearch,
  onClientSearchChange,
  clientDropdownOpen,
  onClientDropdownOpen,
  onClientDropdownClose,
  filteredClientOptions,
  onClientSelect,
  loadingClients,
  clientsError,
  showClientSearchWarning,
  isPersonaMoral,
  isPersonaFisica,
  isExtranjero,
  regimenOptions,
  usoCfdiOptions,
  loadingCatalogs,
  catalogsError,
  requiresFormaPago,
  constanciaAdjunta,
  fileInputsKey,
  onFileChange,
  onClearFiles,
  whatsappRequired,
  createFeedback,
  createFeedbackTone,
  creatingRecord
}) {
  const [regimenSearch, setRegimenSearch] = useState('');
  const [usoCfdiSearch, setUsoCfdiSearch] = useState('');
  const [regimenDropdownOpen, setRegimenDropdownOpen] = useState(false);
  const [usoCfdiDropdownOpen, setUsoCfdiDropdownOpen] = useState(false);

  const handleComboboxBlur = () => {
    setTimeout(() => onClientDropdownClose?.(), 120);
  };

  const PHONE_LADA_OPTIONS = ['+52'];
  const DEFAULT_PHONE_PREFIX = PHONE_LADA_OPTIONS[0];
  const PHONE_LADA_OPTIONS_SORTED = [...PHONE_LADA_OPTIONS].sort((a, b) => b.length - a.length);

  const sanitizePhoneDigits = value => String(value || '').replace(/[^\d]/g, '');

  const normalizePhoneNumberInput = (rawValue, prefix) => {
    const selectedPrefix = PHONE_LADA_OPTIONS.includes(prefix) ? prefix : DEFAULT_PHONE_PREFIX;
    const prefixDigits = selectedPrefix.replace('+', '');
    let digits = sanitizePhoneDigits(rawValue);

    if (prefixDigits && digits.length > 10 && digits.startsWith(prefixDigits)) {
      digits = digits.slice(prefixDigits.length);
    }

    // Handles +521XXXXXXXXXX inputs pasted in the number field.
    if (selectedPrefix === '+52' && digits.length > 10 && digits.startsWith('1')) {
      digits = digits.slice(1);
    }

    return digits.slice(0, 10);
  };

  const getPhoneParts = value => {
    const raw = String(value || '').trim();
    if (!raw) {
      return { prefix: DEFAULT_PHONE_PREFIX, number: '' };
    }

    const compactValue = raw.replace(/\s+/g, '');
    const prefixedMatch = PHONE_LADA_OPTIONS_SORTED.find(code => compactValue.startsWith(code));
    if (prefixedMatch) {
      return {
        prefix: prefixedMatch,
        number: normalizePhoneNumberInput(compactValue.slice(prefixedMatch.length), prefixedMatch)
      };
    }

    const digits = sanitizePhoneDigits(compactValue);
    if (!digits) {
      return { prefix: DEFAULT_PHONE_PREFIX, number: '' };
    }

    const detectedPrefix = PHONE_LADA_OPTIONS_SORTED.find(code => {
      const prefixDigits = code.replace('+', '');
      return digits.startsWith(prefixDigits) && digits.length > 10;
    });

    if (detectedPrefix) {
      const prefixDigits = detectedPrefix.replace('+', '');
      return {
        prefix: detectedPrefix,
        number: normalizePhoneNumberInput(digits.slice(prefixDigits.length), detectedPrefix)
      };
    }

    return {
      prefix: DEFAULT_PHONE_PREFIX,
      number: normalizePhoneNumberInput(digits, DEFAULT_PHONE_PREFIX)
    };
  };

  const filterCatalogOptions = (options, query, selectedId) => {
    if (!Array.isArray(options)) return [];
    const normalizedQuery = query.trim().toLowerCase();
    let filteredList = normalizedQuery
      ? options.filter(option => {
          const searchValue = option.searchValue || [option.displayLabel, option.label, option.code]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return searchValue.includes(normalizedQuery);
        })
      : options;

    if (selectedId) {
      const alreadyIncluded = filteredList.some(option => option.id === selectedId);
      if (!alreadyIncluded) {
        const selectedOption = options.find(option => option.id === selectedId);
        if (selectedOption) {
          filteredList = [selectedOption, ...filteredList];
        }
      }
    }
    return filteredList;
  };

  const telefonoValidacionParts = getPhoneParts(createForm.telefonoValidacion);
  const whatsappParts = getPhoneParts(createForm.whatsapp);
  const phoneParts = getPhoneParts(createForm.phone);
  const regimenSelectedOption = useMemo(() => {
    if (!createForm.regimenFiscal) return null;
    return regimenOptions.find(option => option.id === createForm.regimenFiscal) || null;
  }, [createForm.regimenFiscal, regimenOptions]);
  const usoCfdiSelectedOption = useMemo(() => {
    if (!createForm.usoCfdi) return null;
    return usoCfdiOptions.find(option => option.id === createForm.usoCfdi) || null;
  }, [createForm.usoCfdi, usoCfdiOptions]);
  const regimenInputValue = regimenSearch;
  const usoCfdiInputValue = usoCfdiSearch;

  const filteredRegimenOptions = useMemo(
    () => filterCatalogOptions(regimenOptions, regimenSearch, createForm.regimenFiscal),
    [regimenOptions, regimenSearch, createForm.regimenFiscal]
  );

  const filteredUsoCfdiOptions = useMemo(
    () => filterCatalogOptions(usoCfdiOptions, usoCfdiSearch, createForm.usoCfdi),
    [usoCfdiOptions, usoCfdiSearch, createForm.usoCfdi]
  );

  useEffect(() => {
    setRegimenSearch('');
  }, [regimenOptions]);

  useEffect(() => {
    setUsoCfdiSearch('');
  }, [usoCfdiOptions]);

  const handleCatalogBlur = setter => {
    setTimeout(() => setter(false), 120);
  };

  const handleRegimenSelect = option => {
    const value = option ? option.id : '';
    onFormChange({ target: { name: 'regimenFiscal', value } });
    setRegimenSearch('');
    setRegimenDropdownOpen(false);
  };

  const handleUsoCfdiSelect = option => {
    const value = option ? option.id : '';
    onFormChange({ target: { name: 'usoCfdi', value } });
    setUsoCfdiSearch('');
    setUsoCfdiDropdownOpen(false);
  };

  if (!open) return null;

  const renderFileList = files => {
    if (!files?.length) return null;
    return (
      <ul className="upload-list">
        {files.map(file => (
          <li key={`${file.name}-${file.lastModified}`}>
            <span>{file.name}</span>
            <small>{formatFileSize(file.size)}</small>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="overlay" role="dialog" aria-modal="true">
      <form className="create-card" onSubmit={onSubmit}>
        <div className="form-header">
          <div>
            <h3>Registrar nueva factura</h3>
            <p>Replica el flujo de Fillout en dos pasos: solicitud y método de contacto.</p>
          </div>
          <button type="button" className="link-btn" onClick={onClose}>Cerrar</button>
        </div>

        <div className="create-stepper" role="tablist" aria-label="Progreso de captura">
          <span className={`step ${createStep === 1 ? 'active' : ''}`}>1. Solicitud</span>
          <span className={`step ${createStep === 2 ? 'active' : ''}`}>2. Método de contacto</span>
        </div>

        {createStep === 1 && (
          <>
            <div className="form-section">
              <h4>Datos del contribuyente</h4>
              <div className="form-grid two">
                <label className="full-width">
                  Emisor
                  <div
                    className={`search-combobox ${clientDropdownOpen ? 'open' : ''}`}
                    onBlur={handleComboboxBlur}
                  >
                    <input
                      type="search"
                      placeholder={loadingClients ? 'Cargando clientes...' : 'Filtrar por alias, empresa o ID cliente'}
                      value={clientSearch}
                      onChange={onClientSearchChange}
                      onFocus={() => onClientDropdownOpen?.()}
                      disabled={loadingClients}
                    />
                    {clientDropdownOpen && filteredClientOptions.length > 0 && (
                      <ul className="combobox-options">
                        {filteredClientOptions.map(option => (
                          <li
                            key={option.id}
                            onMouseDown={() => onClientSelect(option)}
                          >
                            {getClientLabel(option)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {!loadingClients && clientsError && (
                    <span className="helper-text error">{clientsError}</span>
                  )}
                  {showClientSearchWarning && (
                    <span className="helper-text warning">No encontramos coincidencias con tu búsqueda.</span>
                  )}
                </label>
                <label className="full-width">
                  Receptor
                  <input
                    type="text"
                    name="clientName"
                    value={createForm.clientName}
                    onChange={onFormChange}
                    required
                  />
                </label>
                <label>
                  Tipo de contribuyente
                  <select name="tipoContribuyente" value={createForm.tipoContribuyente} onChange={onFormChange}>
                    <option value="Persona Moral (Empresa)">Persona Moral (Empresa)</option>
                    <option value="Persona Física">Persona Física</option>
                    <option value="Extranjero">Extranjero</option>
                  </select>
                </label>
                {isPersonaMoral && (
                  <label>
                    RFC (Persona Moral)
                    <input
                      type="text"
                      name="rfcPm"
                      value={createForm.rfcPm}
                      onChange={onFormChange}
                      placeholder="Ej. DIRA900101AB1"
                      maxLength={12}
                    />
                  </label>
                )}
                {isPersonaFisica && (
                  <label>
                    RFC (Persona Física)
                    <input
                      type="text"
                      name="rfcPf"
                      value={createForm.rfcPf}
                      onChange={onFormChange}
                      placeholder="Ej. CAS200310MXA"
                      maxLength={13}
                    />
                  </label>
                )}
                {isExtranjero && (
                  <label>
                    ID Fiscal (Opcional)
                    <input
                      type="text"
                      name="idFiscal"
                      value={createForm.idFiscal}
                      onChange={onFormChange}
                      placeholder="Ej. TAX-ID-12345"
                    />
                  </label>
                )}
                <label>
                  Código postal
                  <input
                    type="text"
                    name="codigoPostal"
                    value={createForm.codigoPostal}
                    onChange={event => {
                      const digits = event.target.value.replace(/[^\d]/g, '').slice(0, 5);
                      onFormChange({ target: { name: 'codigoPostal', value: digits } });
                    }}
                    inputMode="numeric"
                    maxLength={5}
                    pattern="\d{5}"
                  />
                </label>
                <label>
                  Régimen Fiscal (SAT)
                  <div
                    className={`search-combobox ${regimenDropdownOpen ? 'open' : ''}`}
                    onBlur={() => handleCatalogBlur(setRegimenDropdownOpen)}
                  >
                    <input
                      type="search"
                      placeholder={loadingCatalogs ? 'Cargando catálogos...' : 'Buscar por código o nombre'}
                      value={regimenInputValue}
                      onFocus={event => {
                        event.target.select();
                        setRegimenDropdownOpen(true);
                      }}
                      onChange={event => {
                        setRegimenSearch(event.target.value);
                        if (!regimenDropdownOpen) {
                          setRegimenDropdownOpen(true);
                        }
                      }}
                      disabled={!regimenOptions.length || loadingCatalogs}
                    />
                    {regimenDropdownOpen && filteredRegimenOptions.length > 0 && (
                      <ul className="combobox-options">
                        {createForm.regimenFiscal && (
                          <li
                            className="clear-option"
                            onMouseDown={() => handleRegimenSelect(null)}
                          >
                            Limpiar selección
                          </li>
                        )}
                        {filteredRegimenOptions.map(option => (
                          <li
                            key={option.id}
                            onMouseDown={() => handleRegimenSelect(option)}
                          >
                            <strong>{option.code || '—'}</strong>{' '}
                            <span>{option.label}</span>
                            {option.tipo && <small>{option.tipo}</small>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {!!regimenSearch.trim() && !filteredRegimenOptions.length && (
                    <span className="helper-text warning">Sin coincidencias con tu búsqueda.</span>
                  )}
                  {regimenSelectedOption && (
                    <div className="selection-preview">
                      <div>
                        <strong>{regimenSelectedOption.code || '—'}</strong>{' '}
                        <span>{regimenSelectedOption.label}</span>
                        {regimenSelectedOption.tipo && (
                          <small>{`Tipo: ${regimenSelectedOption.tipo}`}</small>
                        )}
                      </div>
                      <button
                        type="button"
                        className="link-btn"
                        onClick={() => handleRegimenSelect(null)}
                      >
                        Borrar
                      </button>
                    </div>
                  )}
                </label>
                <label>
                  Uso del CFDI
                  <div
                    className={`search-combobox ${usoCfdiDropdownOpen ? 'open' : ''}`}
                    onBlur={() => handleCatalogBlur(setUsoCfdiDropdownOpen)}
                  >
                    <input
                      type="search"
                      placeholder={loadingCatalogs ? 'Cargando catálogos...' : 'Buscar por clave o descripción'}
                      value={usoCfdiInputValue}
                      onFocus={event => {
                        event.target.select();
                        setUsoCfdiDropdownOpen(true);
                      }}
                      onChange={event => {
                        setUsoCfdiSearch(event.target.value);
                        if (!usoCfdiDropdownOpen) {
                          setUsoCfdiDropdownOpen(true);
                        }
                      }}
                      disabled={!usoCfdiOptions.length || loadingCatalogs}
                    />
                    {usoCfdiDropdownOpen && filteredUsoCfdiOptions.length > 0 && (
                      <ul className="combobox-options">
                        {createForm.usoCfdi && (
                          <li
                            className="clear-option"
                            onMouseDown={() => handleUsoCfdiSelect(null)}
                          >
                            Limpiar selección
                          </li>
                        )}
                        {filteredUsoCfdiOptions.map(option => (
                          <li
                            key={option.id}
                            onMouseDown={() => handleUsoCfdiSelect(option)}
                          >
                            <strong>{option.code || '—'}</strong>{' '}
                            <span>{option.label}</span>
                            {option.tipo && <small>{option.tipo}</small>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {!!usoCfdiSearch.trim() && !filteredUsoCfdiOptions.length && (
                    <span className="helper-text warning">Sin coincidencias con tu búsqueda.</span>
                  )}
                  {usoCfdiSelectedOption && (
                    <div className="selection-preview">
                      <div>
                        <strong>{usoCfdiSelectedOption.code || '—'}</strong>{' '}
                        <span>{usoCfdiSelectedOption.label}</span>
                        {usoCfdiSelectedOption.tipo && (
                          <small>{`Tipo: ${usoCfdiSelectedOption.tipo}`}</small>
                        )}
                      </div>
                      <button
                        type="button"
                        className="link-btn"
                        onClick={() => handleUsoCfdiSelect(null)}
                      >
                        Borrar
                      </button>
                    </div>
                  )}
                </label>
                {catalogsError && (
                  <p className="helper-text warning full-width">{catalogsError}</p>
                )}
                <label>
                  Periodo a facturar
                  <input
                    type="text"
                    name="periodo"
                    value={createForm.periodo}
                    onChange={onFormChange}
                    placeholder="Ej. Noviembre 2025"
                  />
                </label>
              </div>
            </div>

            <div className="form-section">
              <h4>Detalles de la solicitud</h4>
              <div className="info-box">
                <ul>
                  <li>Si eliges PUE, el comprobante se marca como pagado al momento de emitirlo.</li>
                  <li>Con PPD se requiere complementar el pago posteriormente con un comprobante adicional.</li>
                </ul>
              </div>
              <div className="form-grid two">
                <label>
                  Número de TICKET/OC
                  <input
                    type="text"
                    name="numeroTicket"
                    value={createForm.numeroTicket}
                    onChange={onFormChange}
                    placeholder="Ej. OC-1299"
                  />
                </label>
                <label>
                  Método de pago
                  <select name="metodoPago" value={createForm.metodoPago} onChange={onFormChange}>
                    {metodoPagoOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                {requiresFormaPago && (
                  <label>
                    Forma de pago
                    <select name="formaPago" value={createForm.formaPago} onChange={onFormChange}>
                      {formaPagoOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </label>
                )}
                <label>
                  Total estimado (MXN)
                  <input
                    type="number"
                    name="total"
                    min="0"
                    step="0.01"
                    value={createForm.total}
                    onChange={onFormChange}
                    required
                  />
                </label>
                <label>
                  Estado de solicitud
                  <select name="estado" value={createForm.estado} onChange={onFormChange}>
                    {estadoOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="full-width">
                Concepto de venta o descripción
                <textarea
                  name="concepto"
                  rows={3}
                  value={createForm.concepto}
                  onChange={onFormChange}
                  placeholder="Describe los artículos o servicios a facturar"
                />
              </label>
              <label className="full-width">
                Comentarios visibles para operaciones
                <textarea
                  name="comentarios"
                  rows={2}
                  value={createForm.comentarios}
                  onChange={onFormChange}
                  placeholder="Ej. Cliente solicita confirmación antes de enviar CFDI"
                />
              </label>
              <label className="full-width">
                Notas internas del equipo
                <textarea
                  name="notasInternas"
                  rows={2}
                  value={createForm.notasInternas}
                  onChange={onFormChange}
                  placeholder="Solo visible para el equipo: priorización, pendientes, etc."
                />
              </label>
            </div>

            <div className="form-section">
              <h4>Documentación y respaldo</h4>
              <div className="form-grid two">
                <label className="full-width upload-field">
                  Constancia fiscal (PDF/Imagen)
                  <input
                    key={`constancia-${fileInputsKey}`}
                    type="file"
                    name="constanciaFiles"
                    accept="application/pdf,image/*"
                    multiple
                    onChange={onFileChange}
                  />
                  <span className="helper-text">Puedes subir hasta 5 archivos; convertimos automáticamente a adjuntos de Airtable.</span>
                  {renderFileList(createForm.constanciaFiles)}
                  {createForm.constanciaFiles?.length > 0 && (
                    <button type="button" className="link-btn" onClick={() => onClearFiles('constanciaFiles')}>
                      Quitar constancias
                    </button>
                  )}
                </label>
                {constanciaAdjunta ? (
                  <label>
                    Teléfono cuando sí adjuntas constancia
                    <div className="phone-input-group">
                      <select
                        className="phone-lada-select"
                        value={phoneParts.prefix}
                        onChange={event => {
                          const nextPrefix = event.target.value;
                          const currentNumber = phoneParts.number;
                          const nextValue = currentNumber ? `${nextPrefix}${currentNumber}` : nextPrefix;
                          onFormChange({ target: { name: 'phone', value: nextValue } });
                        }}
                      >
                        {PHONE_LADA_OPTIONS.map(code => (
                          <option key={code} value={code}>{code}</option>
                        ))}
                      </select>
                      <input
                        className="phone-number-input"
                        type="tel"
                        name="phone"
                        value={phoneParts.number}
                        onChange={event => {
                          const digits = normalizePhoneNumberInput(event.target.value, phoneParts.prefix);
                          const currentPrefix = phoneParts.prefix;
                          const nextValue = digits ? `${currentPrefix}${digits}` : currentPrefix;
                          onFormChange({ target: { name: 'phone', value: nextValue } });
                        }}
                        placeholder="6141689133"
                        inputMode="tel"
                        maxLength={10}
                      />
                    </div>
                  </label>
                ) : (
                  <label>
                    Teléfono obligatorio si no adjuntas constancia
                    <div className="phone-input-group">
                      <select
                        className="phone-lada-select"
                        value={telefonoValidacionParts.prefix}
                        onChange={event => {
                          const nextPrefix = event.target.value;
                          const nextValue = telefonoValidacionParts.number
                            ? `${nextPrefix}${telefonoValidacionParts.number}`
                            : nextPrefix;
                          onFormChange({ target: { name: 'telefonoValidacion', value: nextValue } });
                        }}
                      >
                        {PHONE_LADA_OPTIONS.map(code => (
                          <option key={code} value={code}>{code}</option>
                        ))}
                      </select>
                      <input
                        className="phone-number-input"
                        type="tel"
                        name="telefonoValidacion"
                        value={telefonoValidacionParts.number}
                        onChange={event => {
                          const digits = normalizePhoneNumberInput(event.target.value, telefonoValidacionParts.prefix);
                          const nextValue = digits
                            ? `${telefonoValidacionParts.prefix}${digits}`
                            : telefonoValidacionParts.prefix;
                          onFormChange({ target: { name: 'telefonoValidacion', value: nextValue } });
                        }}
                        placeholder="6141689133"
                        required
                        inputMode="tel"
                        maxLength={10}
                      />
                    </div>
                    <span className="helper-text">Es necesario porque no adjuntaste constancia fiscal.</span>
                  </label>
                )}
                <label className="full-width upload-field">
                  Ticket/OC (PDF/Imagen)
                  <input
                    key={`ticket-${fileInputsKey}`}
                    type="file"
                    name="ticketFiles"
                    accept="application/pdf,image/*"
                    multiple
                    onChange={onFileChange}
                  />
                  {renderFileList(createForm.ticketFiles)}
                  {createForm.ticketFiles?.length > 0 && (
                    <button type="button" className="link-btn" onClick={() => onClearFiles('ticketFiles')}>
                      Quitar tickets
                    </button>
                  )}
                </label>
              </div>
            </div>
          </>
        )}

        {createStep === 2 && (
          <>
            <div className="form-section">
              <h4>Método de contacto</h4>
              <div className="form-grid two">
                <label>
                  Medio preferido
                  <select name="contact" value={createForm.contact} onChange={onFormChange}>
                    {contactOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Correo electrónico
                  <input
                    type="email"
                    name="email"
                    value={createForm.email}
                    onChange={onFormChange}
                    required
                  />
                </label>
                <label className="full-width">
                  WhatsApp
                  <div className="phone-input-group">
                    <select
                      className="phone-lada-select"
                      value={whatsappParts.prefix}
                        onChange={event => {
                        const nextPrefix = event.target.value;
                        const nextValue = whatsappParts.number
                          ? `${nextPrefix}${whatsappParts.number}`
                          : nextPrefix;
                        onFormChange({ target: { name: 'whatsapp', value: nextValue } });
                      }}
                    >
                      {PHONE_LADA_OPTIONS.map(code => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </select>
                    <input
                      className="phone-number-input"
                      type="tel"
                      name="whatsapp"
                      value={whatsappParts.number}
                      onChange={event => {
                        const digits = normalizePhoneNumberInput(event.target.value, whatsappParts.prefix);
                        const nextValue = digits
                          ? `${whatsappParts.prefix}${digits}`
                          : whatsappParts.prefix;
                        onFormChange({ target: { name: 'whatsapp', value: nextValue } });
                      }}
                      onBlur={onWhatsappBlur}
                      placeholder="6141689133"
                      required={whatsappRequired}
                      inputMode="tel"
                      maxLength={10}
                    />
                  </div>
                  <span className="helper-text">
                    {whatsappRequired ? 'Obligatorio si eliges WhatsApp o Ambos.' : 'Opcional cuando el contacto será por correo.'}
                  </span>
                </label>
              </div>
            </div>
            <div className="info-box notice">
              <p>Tu comprobante se enviará en un lapso de 24 horas hábiles. Para dudas llama al (614)533-9367 vía WhatsApp.</p>
            </div>
          </>
        )}

        {createFeedback && (
          <p className={`helper-text ${createFeedbackTone}`}>{createFeedback}</p>
        )}

        <div className="form-actions">
          {createStep === 1 && (
            <>
              <button type="button" className="btn ghost" onClick={onClose}>Cancelar</button>
              <button type="button" className="btn primary" onClick={onNextStep}>Siguiente</button>
            </>
          )}
          {createStep === 2 && (
            <>
              <button type="button" className="btn ghost" onClick={onPrevStep} disabled={creatingRecord}>Regresar</button>
              <button className="btn primary" type="submit" disabled={creatingRecord}>
                {creatingRecord ? 'Guardando...' : 'Guardar en Airtable'}
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}

export default CreateInvoiceModal;
